import React from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  IconButton,
} from '@material-ui/core';

import CloseIcon from '@material-ui/icons/Close';
import { useStyles } from '../../styles';
import '../../styles/enhanceTestPlanModal.css';
import type { IqeProject } from '../../interfaces/ui';

interface EnhanceTestPlanModalProps {
  open: boolean;
  onClose: () => void;
  onRunTests: (projectId: string) => void;
  project: IqeProject;
  isToolAdmin: boolean;
}

export default function EnhanceTestPlanModal({
  open,
  onClose,
  onRunTests,
  project,
  isToolAdmin,
}: EnhanceTestPlanModalProps) {
  const classes = useStyles();
  const [testCaseContext, setTestCaseContext] = React.useState('');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: `${classes.enhanceTestPlanPaper} ets-paper` }}
    >
      <DialogTitle disableTypography className="ets-header">
        <Typography variant="h6" className="ets-header-title">
          Enhance Test Plan
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close"
          className="ets-close-btn"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className="ets-content">
        <Typography variant="body2" className="ets-description">
          Describe how you would like the AI to improve the existing test suite.
          Additional test cases may be generated, refined, deduplicated, and
          executed automatically.
        </Typography>
        <Box className="ets-form-group">
          <Typography component="label" className="ets-label">
            Improvement Request
          </Typography>
          <TextField
            multiline
            rows={5}
            variant="outlined"
            fullWidth
            placeholder={`Examples:\n\n• Generate 20 additional test cases for checkout scenarios\n\n• Add negative test scenarios\n\n• Include accessibility validations`}
            value={testCaseContext}
            onChange={e => setTestCaseContext(e.target.value)}
            className="ets-textarea"
          />
        </Box>
        <Typography className="ets-helper-text">
          The AI will use these instructions to enhance the most recent test
          suite before starting a new execution run.
        </Typography>
      </DialogContent>
      <DialogActions className="ets-actions">
        <Button variant="outlined" className="ets-cancel-btn" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          className="ets-submit-btn"
          onClick={() => {
            if (testCaseContext.trim() && isToolAdmin) {
              onRunTests(project.id);
              onClose();
              setTestCaseContext('');
            }
          }}
          disabled={!isToolAdmin}
        >
          Improve & Execute
        </Button>
      </DialogActions>
    </Dialog>
  );
}
